import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  deleteAssociatedUser,
  patchAssociatedUser,
  postAddUser,
} from "../../../../apis";
import Check from "../../../../assets/icons/Check";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import DownArrow from "../../../../assets/icons/DownArrow";
import { InputEditable } from "../../../../components/common/Input";
import { EMIAL_REGEX } from "../../../../constants";
import useOutsideClick from "../../../../hooks/useOutsideClick";

const CreateUser = ({
  id,
  email,
  role,
  setUsers = () => {},
  callGetUser = () => {},
}) => {
  const [userEmail, setUserEmail] = useState(email ? email : "");
  const [userRole, setUseRole] = useState(role ? role : 1);
  const [showDropdown, setShowDropdown] = useState(false);
  const [saveButtonDisable, setSaveButtonDisable] = useState(false);

  const dropdownRef = useRef();

  const closeDropdown = () => setShowDropdown(false);

  function downloadTxtFile(data) {
    const fileContent = JSON.stringify(data, null, 2);
    const blob = new Blob([fileContent], { type: "text/plain" });
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = "credentials.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const onAutoSave = (role) => {
    setTimeout(async () => {
      if (id) {
        try {
          await patchAssociatedUser({ id: id, type: role });
          window.Toast.fire({
            icon: "success",
            title: "User updated successfully.",
          });
        } catch (error) {
          console.error(error);
          window.Toast.fire({
            icon: "error",
            title: "Something went wrong",
          });
        }
      }
    }, 500);
  };

  const onSave = async () => {
    try {
      if (EMIAL_REGEX.exec(userEmail) === null) {
        window.Toast.fire({
          icon: "error",
          title: "Please enter a valid email",
        });
        return;
      }
      setSaveButtonDisable(true);
      const { data } = await postAddUser({
        email: userEmail,
        type: userRole,
      });
      window.Toast.fire({
        icon: "success",
        title: "User added successfully.",
      });
      setUserEmail("");
      setUseRole(1);
      setSaveButtonDisable(false);
      downloadTxtFile({ email: data.email, password: data.password });
      setUsers((prev) => [...prev, { ...data, user_role: userRole }]);
    } catch (error) {
      console.log(error);
      if (error.response && error.response.data) {
        window.Toast.fire({
          icon: "error",
          title: error.response.data.message ?? "Something went wrong",
        });
        setSaveButtonDisable(false);
        return;
      }
      window.Toast.fire({
        icon: "error",
        title: "Something went wrong",
      });
      setSaveButtonDisable(false);
    }
  };

  const onDelete = () => {
    if (id) {
      Swal.fire({
        title: "Delete User",
        text: "Are you sure you want to delete this user? Once deleted, a user cannot be restored.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
        showLoaderOnConfirm: true,
        preConfirm: () => {
          return deleteAssociatedUser(id)
            .then(async (response) => {
              await callGetUser();
              window.Toast.fire({
                icon: "success",
                title: "User has been successfully deleted",
              });
            })
            .catch((err) => {
              console.error(err);
              Swal.fire(
                "Error!",
                err.response?.data?.message ?? "Something went wrong",
                "error"
              );
            });
        },
      });
    } else {
      window.Toast.fire({
        icon: "error",
        title: "Something went wrong",
      });
    }
  };

  useOutsideClick(dropdownRef, closeDropdown);

  useEffect(() => {
    if (userEmail && !id) setSaveButtonDisable(false);
    else setSaveButtonDisable(true);
  }, [userEmail]);

  return (
    <div
      className={`grid auto-cols-max grid-cols-[2.9fr_1.6fr_1fr_1fr_1fr_1fr_1fr] gap-6 ml-2 mt-3 border-b border-zinc-700 text-sm p-3`}
    >
      <div className="export  truncate">
        <div className="flex">
          {id ? (
            <button
              className={`font-medium cursor-pointer  rounded-xl pr-3   w-[40px] `}
              onClick={onDelete}
            >
              <DeleteIcon />
            </button>
          ) : (
            <button
              className={`font-medium  text-center  rounded-xl  pr-3  w-[40px]  ${
                saveButtonDisable
                  ? "cursor-not-allowed opacity-75"
                  : "cursor-pointer"
              }`}
              onClick={onSave}
              disabled={saveButtonDisable}
            >
              <Check />
            </button>
          )}
          {id ? (
            <div title={email}>{email} </div>
          ) : (
            <input
              className=" bg-transparent py-2 placeholder:text-secondary-500  placeholder:text-sm outline-none border-b-[1px] border-b-white w-3/4 text-light-gray text-sm "
              name="userEmail"
              onChange={(e) => setUserEmail(e.target.value)}
              value={userEmail}
              placeholder="Enter  email"
            />
          )}
        </div>
      </div>
      <div className="export text-left  break-words ">
        <div
          ref={dropdownRef}
          onClick={(e) => {
            e.stopPropagation();
            setShowDropdown((prev) => !prev);
          }}
          className="flex items-center shrink-0 justify-between whitespace-nowrap text-primary-green w-[120px]"
        >
          <div className="relative flex items-center mr-5 text-xs underline underline-offset-4 cursor-pointer sharing-card">
            <span className="font-semibold">
              {userRole === 1 ? "Co-admin" : "Uploader"}
            </span>
            <span className="ml-2">
              <DownArrow rotate={showDropdown} />
            </span>
            {showDropdown ? (
              <div className="absolute light:bg-theme-light bg-dark-gray shadow-dark-input w-[150px] left-1/2 -translate-x-1/2 top-[20px] rounded-[10px] overflow-hidden z-40 mt-3">
                <div className="flex flex-col  text-light-gray text-sm">
                  <div
                    onClick={() => {
                      setUseRole(1);
                      if (id) onAutoSave(1);
                    }}
                    className="whitespace-nowrap px-4 py-3 cursor-pointer hover:bg-black hover:text-primary-green "
                  >
                    Co-Admin
                  </div>
                  <div
                    onClick={() => {
                      setUseRole(2);
                      if (id) onAutoSave(2);
                    }}
                    className="whitespace-nowrap px-4 py-3 hover:bg-black hover:text-primary-green "
                  >
                    Uploader
                  </div>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <div className="">
        <svg
          className=" checkmark_wo_animation h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 52 52"
        >
          <circle
            className="checkmark__circle_wo_animation"
            cx="26"
            cy="26"
            r="25"
            fill="none"
          />
          <path
            className="checkmark__check_wo_animation"
            fill="none"
            d="M14.1 27.2l7.1 7.2 16.7-16.8"
          />
        </svg>
      </div>
      <div className=" ">
        <svg
          className=" checkmark_wo_animation h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 52 52"
        >
          <circle
            className="checkmark__circle_wo_animation"
            cx="26"
            cy="26"
            r="25"
            fill="none"
          />
          <path
            className="checkmark__check_wo_animation"
            fill="none"
            d="M14.1 27.2l7.1 7.2 16.7-16.8"
          />
        </svg>
      </div>
      <div className=" ">
        <svg
          className=" checkmark_wo_animation h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 52 52"
        >
          <circle
            className="checkmark__circle_wo_animation"
            cx="26"
            cy="26"
            r="25"
            fill="none"
          />
          <path
            className="checkmark__check_wo_animation"
            fill="none"
            d="M14.1 27.2l7.1 7.2 16.7-16.8"
          />
        </svg>
      </div>
      <div className={" "}>
        {userRole === 1 ? (
          <svg
            className=" checkmark_wo_animation h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 52 52"
          >
            <circle
              className="checkmark__circle_wo_animation"
              cx="26"
              cy="26"
              r="25"
              fill="none"
            />
            <path
              className="checkmark__check_wo_animation"
              fill="none"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
            />
          </svg>
        ) : (
          <></>
        )}
      </div>
      <div className="  ">
        {userRole === 1 ? (
          <svg
            className=" checkmark_wo_animation h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 52 52"
          >
            <circle
              className="checkmark__circle_wo_animation"
              cx="26"
              cy="26"
              r="25"
              fill="none"
            />
            <path
              className="checkmark__check_wo_animation"
              fill="none"
              d="M14.1 27.2l7.1 7.2 16.7-16.8"
            />
          </svg>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default CreateUser;
